import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { appointmentService } from '../services/api';
import { AppointmentFormData } from '../types';
import './AppointmentForm.css';

interface AppointmentFormProps {
  mode: 'create' | 'edit';
}

interface ValidationErrors {
  name?: string;
  surname?: string;
  type?: string;
  phone?: string;
  email?: string;
  date?: string;
  time?: string;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(mode === 'edit');
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<AppointmentFormData>({
    gender: 'M',
    name: '',
    surname: '',
    type: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    simulateSMS: false,
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchAppointment = async () => {
      if (mode === 'edit' && id) {
        try {
          const data = await appointmentService.getById(parseInt(id));
          setFormData({
            gender: data.gender,
            name: data.name,
            surname: data.surname,
            type: data.type,
            phone: data.phone,
            email: data.email,
            date: data.date,
            time: data.time,
            simulateSMS: false,
          });
          setLoading(false);
        } catch (err) {
          setError('Erreur lors du chargement du rendez-vous');
          setLoading(false);
          console.error(err);
        }
      }
    };

    fetchAppointment();
  }, [id, mode]);

  // Fonction de validation des champs du formulaire
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        return value.trim() === '' ? 'Le prénom est requis' : 
               value.length < 2 ? 'Le prénom doit contenir au moins 2 caractères' : undefined;
      case 'surname':
        return value.trim() === '' ? 'Le nom est requis' : 
               value.length < 2 ? 'Le nom doit contenir au moins 2 caractères' : undefined;
      case 'type':
        return value === '' ? 'Le type de rendez-vous est requis' : undefined;
      case 'phone':
        const phoneRegex = /^[0-9]{10}$/;
        return value.trim() === '' ? 'Le numéro de téléphone est requis' : 
               !phoneRegex.test(value.replace(/\D/g, '')) ? 'Le numéro de téléphone doit contenir 10 chiffres' : undefined;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return value.trim() === '' ? 'L\'email est requis' : 
               !emailRegex.test(value) ? 'L\'email n\'est pas valide' : undefined;
      case 'date':
        if (value === '') return 'La date est requise';
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate < today ? 'La date ne peut pas être dans le passé' : undefined;
      case 'time':
        return value === '' ? 'L\'heure est requise' : undefined;
      default:
        return undefined;
    }
  };

  // Valider tous les champs du formulaire
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    Object.entries(formData).forEach(([key, value]) => {
      if (key !== 'gender') { // La civilité a toujours une valeur par défaut
        const error = validateField(key, value);
        if (error) {
          errors[key as keyof ValidationErrors] = error;
          isValid = false;
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Valider le champ lorsqu'il est modifié
    if (touched[name]) {
      const error = validateField(name, value);
      setValidationErrors(prev => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));

    // Valider le champ lorsqu'il perd le focus
    const error = validateField(name, value);
    setValidationErrors(prev => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Marquer tous les champs comme touchés
    const allTouched: Record<string, boolean> = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Valider tous les champs avant de soumettre
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === 'create') {
        await appointmentService.create(formData);
      } else if (mode === 'edit' && id) {
        await appointmentService.update(parseInt(id), formData);
      }
      navigate('/appointments');
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du rendez-vous');
      setLoading(false);
      console.error(err);
    }
  };

  if (loading && mode === 'edit') {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="appointment-form-container">
      <h1>{mode === 'create' ? 'Nouveau rendez-vous' : 'Modifier le rendez-vous'}</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit} className="appointment-form">
        <div className="form-group">
          <label htmlFor="gender">Civilité</label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            required
          >
            <option value="M">Monsieur</option>
            <option value="F">Madame</option>
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Prénom</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={validationErrors.name && touched.name ? 'input-error' : ''}
              required
            />
            {validationErrors.name && touched.name && (
              <div className="error-text">{validationErrors.name}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="surname">Nom</label>
            <input
              type="text"
              id="surname"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              onBlur={handleBlur}
              className={validationErrors.surname && touched.surname ? 'input-error' : ''}
              required
            />
            {validationErrors.surname && touched.surname && (
              <div className="error-text">{validationErrors.surname}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="type">Type de rendez-vous</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            onBlur={handleBlur}
            className={validationErrors.type && touched.type ? 'input-error' : ''}
            required
          >
            <option value="">Sélectionnez un type</option>
            <option value="HC">HC (Hospitalisation Complète)</option>
            <option value="HDJ">HDJ (Hospitalisation De Jour)</option>
          </select>
          {validationErrors.type && touched.type && (
            <div className="error-text">{validationErrors.type}</div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Date</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              onBlur={handleBlur}
              className={validationErrors.date && touched.date ? 'input-error' : ''}
              required
            />
            {validationErrors.date && touched.date && (
              <div className="error-text">{validationErrors.date}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="time">Heure</label>
            <input
              type="time"
              id="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              onBlur={handleBlur}
              className={validationErrors.time && touched.time ? 'input-error' : ''}
              required
            />
            {validationErrors.time && touched.time && (
              <div className="error-text">{validationErrors.time}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={validationErrors.email && touched.email ? 'input-error' : ''}
            required
          />
          {validationErrors.email && touched.email && (
            <div className="error-text">{validationErrors.email}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Téléphone</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={validationErrors.phone && touched.phone ? 'input-error' : ''}
            required
          />
          {validationErrors.phone && touched.phone && (
            <div className="error-text">{validationErrors.phone}</div>
          )}
        </div>

        {/* Option de simulation SMS - uniquement disponible en mode création */}
        {mode === 'create' && (
          <div className="form-group simulation-toggle">
            <label className="simulation-label">
              <input
                type="checkbox"
                id="simulateSMS"
                name="simulateSMS"
                checked={formData.simulateSMS}
                onChange={handleCheckboxChange}
              />
              <span className="simulation-text">
                Mode simulation SMS {formData.simulateSMS ? '(activé)' : '(désactivé)'}
              </span>
            </label>
            <small className="form-text">
              En mode simulation, aucun SMS réel n'est envoyé et aucun crédit n'est consommé
            </small>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/appointments')}>
            Annuler
          </button>
          <button 
            type="submit" 
            className={`btn ${formData.simulateSMS ? 'btn-warning' : 'btn-primary'}`} 
            disabled={loading}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AppointmentForm;
